import type { TrelloCloudContextSession } from "./trello-authorization";

export const MAX_UPLOADED_RESTORE_BYTES = 32 * 1024 * 1024;
export const MAX_UPLOADED_RESTORE_ENTRIES = 500;

export interface UploadedRestoreImport {
  archiveByteSize: number;
  archiveSha256: string;
  expiresAt: string;
  importId: string;
  originalSnapshotId: string | null;
  restoreSnapshotId: string | null;
  sourceBoardId: string | null;
  status:
    | "pending_upload"
    | "uploaded"
    | "validating"
    | "ready"
    | "invalid"
    | "expired"
    | "aborted";
  validationCode: string | null;
  workerValidationLimitBytes: number;
}

export class UploadedRestoreRequestError extends Error {
  readonly retryable: boolean;

  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "UploadedRestoreRequestError";
    this.retryable = status === 408 || status === 429 || status >= 500;
  }
}

function parseImport(value: unknown): UploadedRestoreImport {
  if (
    typeof value !== "object" ||
    value === null ||
    !("importId" in value) ||
    typeof value.importId !== "string" ||
    !("status" in value) ||
    ![
      "pending_upload",
      "uploaded",
      "validating",
      "ready",
      "invalid",
      "expired",
      "aborted",
    ].includes(String(value.status)) ||
    !("archiveByteSize" in value) ||
    typeof value.archiveByteSize !== "number" ||
    !("archiveSha256" in value) ||
    typeof value.archiveSha256 !== "string" ||
    !("expiresAt" in value) ||
    typeof value.expiresAt !== "string" ||
    !("workerValidationLimitBytes" in value) ||
    value.workerValidationLimitBytes !== MAX_UPLOADED_RESTORE_BYTES
  ) {
    throw new Error(
      "Cloud Preserve returned an invalid uploaded restore response.",
    );
  }
  return value as UploadedRestoreImport;
}

async function requestImport(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  path: string,
  init: RequestInit,
  fetchImplementation: typeof fetch,
): Promise<UploadedRestoreImport> {
  if (!apiUrl)
    throw new Error("Cloud Preserve API is not configured for this build.");
  const response = await fetchImplementation(`${apiUrl}${path}`, {
    ...init,
    credentials: "omit",
    headers: { ...init.headers, authorization: `Bearer ${session.contextJwt}` },
    referrerPolicy: "no-referrer",
  });
  const payload = (await response.json()) as unknown;
  if (!response.ok) {
    const detail =
      typeof payload === "object" &&
      payload !== null &&
      "detail" in payload &&
      typeof payload.detail === "string"
        ? payload.detail
        : "Cloud Preserve could not process the uploaded restore archive.";
    throw new UploadedRestoreRequestError(response.status, detail);
  }
  return parseImport(payload);
}

export function declareUploadedRestore(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  input: {
    archiveByteSize: number;
    archiveSha256: string;
    fileName: string;
    idempotencyKey: string;
  },
  fetchImplementation: typeof fetch = fetch,
): Promise<UploadedRestoreImport> {
  return requestImport(
    apiUrl,
    session,
    "/v1/cloud/boards/current/restore-imports",
    {
      body: JSON.stringify(input),
      headers: { "content-type": "application/json" },
      method: "POST",
    },
    fetchImplementation,
  );
}

export function uploadRestoreArchive(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  importId: string,
  file: File,
  signal: AbortSignal,
  fetchImplementation: typeof fetch = fetch,
): Promise<UploadedRestoreImport> {
  return requestImport(
    apiUrl,
    session,
    `/v1/cloud/boards/current/restore-imports/${encodeURIComponent(importId)}/archive`,
    {
      body: file,
      headers: { "content-type": "application/zip" },
      method: "PUT",
      signal,
    },
    fetchImplementation,
  );
}

export function validateRestoreArchive(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  importId: string,
  fetchImplementation: typeof fetch = fetch,
  signal?: AbortSignal,
): Promise<UploadedRestoreImport> {
  return requestImport(
    apiUrl,
    session,
    `/v1/cloud/boards/current/restore-imports/${encodeURIComponent(importId)}/validation`,
    { method: "POST", ...(signal ? { signal } : {}) },
    fetchImplementation,
  );
}

export function readUploadedRestoreStatus(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  importId: string,
  fetchImplementation: typeof fetch = fetch,
  signal?: AbortSignal,
): Promise<UploadedRestoreImport> {
  return requestImport(
    apiUrl,
    session,
    `/v1/cloud/boards/current/restore-imports/${encodeURIComponent(importId)}`,
    { method: "GET", ...(signal ? { signal } : {}) },
    fetchImplementation,
  );
}

export function abortUploadedRestore(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  importId: string,
  fetchImplementation: typeof fetch = fetch,
): Promise<UploadedRestoreImport> {
  return requestImport(
    apiUrl,
    session,
    `/v1/cloud/boards/current/restore-imports/${encodeURIComponent(importId)}`,
    { method: "DELETE" },
    fetchImplementation,
  );
}

export async function rebindUploadedRestoreJob(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  importId: string,
  jobId: string,
  fetchImplementation: typeof fetch = fetch,
): Promise<{ jobId: string; snapshotId: string }> {
  if (!apiUrl)
    throw new Error("Cloud Preserve API is not configured for this build.");
  const response = await fetchImplementation(
    `${apiUrl}/v1/cloud/boards/current/restore-imports/${encodeURIComponent(importId)}/restore-jobs/${encodeURIComponent(jobId)}`,
    {
      credentials: "omit",
      headers: { authorization: `Bearer ${session.contextJwt}` },
      method: "POST",
      referrerPolicy: "no-referrer",
    },
  );
  const payload = (await response.json()) as unknown;
  if (!response.ok) {
    const detail =
      typeof payload === "object" &&
      payload !== null &&
      "detail" in payload &&
      typeof payload.detail === "string"
        ? payload.detail
        : "Cloud Preserve could not attach the replacement ZIP.";
    throw new Error(detail);
  }
  if (
    typeof payload !== "object" ||
    payload === null ||
    !("jobId" in payload) ||
    typeof payload.jobId !== "string" ||
    !("snapshotId" in payload) ||
    typeof payload.snapshotId !== "string"
  ) {
    throw new Error(
      "Cloud Preserve returned an invalid ZIP re-upload response.",
    );
  }
  return { jobId: payload.jobId, snapshotId: payload.snapshotId };
}

export async function inspectUploadedRestoreFile(
  file: File,
  signal?: AbortSignal,
): Promise<{ entryCount: number; sha256: string }> {
  if (!file.name.toLowerCase().endsWith(".zip") || file.size <= 0) {
    throw new Error("Choose a Board Preserve ZIP archive.");
  }
  if (file.size > MAX_UPLOADED_RESTORE_BYTES) {
    throw new Error(
      "Uploaded restore currently supports ZIP archives up to 32 MiB.",
    );
  }
  if (signal?.aborted) throw new DOMException("Cancelled", "AbortError");
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (signal?.aborted) throw new DOMException("Cancelled", "AbortError");
  if (bytes.byteLength < 22)
    throw new Error("The selected file is not a complete ZIP archive.");
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const eocd = bytes.byteLength - 22;
  if (
    view.getUint32(eocd, true) !== 0x06054b50 ||
    view.getUint16(eocd + 20, true) !== 0
  ) {
    throw new Error(
      "Choose an unmodified Board Preserve ZIP without trailing data or a ZIP comment.",
    );
  }
  const entryCount = view.getUint16(eocd + 10, true);
  if (entryCount > MAX_UPLOADED_RESTORE_ENTRIES) {
    throw new Error(
      "Uploaded restore currently supports archives containing no more than 500 ZIP entries.",
    );
  }
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", bytes));
  return {
    entryCount,
    sha256: Array.from(digest, (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join(""),
  };
}
